import * as Xstate from "@xstate/react";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { LuArrowLeftToLine, LuArrowRightToLine } from "react-icons/lu";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";

import * as devConfig from "../../app/configs/devConfig";
import { ROUTE_NAMES } from "../../app/constants/TextConstants";
import { getAuthHeader } from "../../app/globalState/GlobalAuthState";
import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import PlusSvg from "../../components/svg/PlusSvg";
import { useUnsavedChangesGuard } from "../../components/UnsavedChangesGuard";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { sideNavRouteMap } from "../../pages/main/SidenavRouteMaps";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useCanViewActivityLogQuery } from "../../slices/ActivityLogSlice";
import { useGetFirmFilesSummaryQuery } from "../../slices/DocumentsSlice";
import {
  useGetSupportAccessQuery,
  useGetSupportCountQuery,
} from "../../slices/SupportSlice";
import { AddContactModal } from "../contacts/modals/addContact/AddContactModal";
import { AddContactType } from "../contacts/modals/addContact/model/addContact.types";
import {
  useCanOpenReports,
  VIEW_BILLING_PERMISSION,
} from "../reports/reportsAccess";
import AddTimeEntry from "../time/AddTimeEntry";
import StartTimer from "../time/StartTimer";
import { AddWorkModal } from "../works/modals/AddWork.modal";
import { CreateMenu } from "./createMenu/CreateMenu";
import { CreateAction } from "./createMenu/createMenu.utils";
import { LocationSelector } from "./LocationSelector";

type MainSidebarNavProps = {
  /** Phones (below md): the menu is hidden unless this is true; then it
   * shows as an overlay over the page. From md up it is always shown. */
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
};

export const MainSidebarNav: React.FC<MainSidebarNavProps> = ({
  isMobileOpen = false,
  onMobileClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [isOpenAdd, setIsOpenAdd] = React.useState(false);
  const [isAddContact, setIsAddContact] = React.useState(false);
  const [contactType, setContactType] =
    React.useState<AddContactType>("individual");
  const [isAddWork, setIsAddWork] = React.useState(false);
  const [isAddTime, setIsAddTime] = React.useState(false);
  const [isStartTimer, setIsStartTimer] = React.useState(false);
  const [user, setUser] = React.useState<{
    userRoleEnum: { userRole: string };
    userProfile: { firstName: string; lastName: string };
    permissionIds?: string[];
  }>();
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const openButtonRef = React.useRef<HTMLDivElement>(null);

  const { runWithUnsavedGuard } = useUnsavedChangesGuard();
  const orgId = getLinkedFirm()?.orgId || "";
  // Admins, or users with "View Activity Log" (Settings > Team).
  const { data: canViewActivityLog } = useCanViewActivityLogQuery(
    { orgId },
    { skip: !orgId },
  );
  // Documents: client uploads nobody has opened yet (my clients when I have any).
  const { data: filesSummary } = useGetFirmFilesSummaryQuery(
    { orgId, mine: true },
    { skip: !orgId, pollingInterval: 120000 },
  );
  // Support desk (owner only): the server says who may use it; badge = conversations waiting for them.
  const { data: supportAccess } = useGetSupportAccessQuery();
  const { data: supportCount } = useGetSupportCountQuery(undefined, {
    skip: !supportAccess?.canUse,
    pollingInterval: 120000,
  });
  // Admins, or users with "View Reports" (everyone before database update Mg-223).
  const canOpenReports = useCanOpenReports(user);
  // Admins, or users with "View Billings": Billing menu, New Invoice, New Payment.
  const canViewBilling =
    user?.userRoleEnum?.userRole === "Admin" ||
    !!user?.permissionIds?.includes(VIEW_BILLING_PERMISSION);
  useOnClickOutside(openButtonRef, () => setIsOpenAdd(false));

  // Create menu (owner 2026-10-25): each row opens the same window or page as before.
  const pickCreate = (action: CreateAction) => {
    setIsOpenAdd(false);
    onMobileClose?.();
    runWithUnsavedGuard(() => {
      if (action === "individual" || action === "organization") {
        setContactType(action);
        setIsAddContact(true);
      } else if (action === "work") {
        setIsAddWork(true);
      } else if (action === "startTimer") {
        setIsStartTimer(true);
      } else if (action === "addTime") {
        setIsAddTime(true);
      } else if (action === "invoice") {
        navigate("/billing/invoices/add");
      } else if (action === "payment") {
        navigate("/billing/payments/add");
      }
    });
  };

  // hide sidebar on bank doc details view
  const docPathMatch = useMatch({
    path: `${ROUTE_NAMES.documents.bankDoc.index}/:docId/*`,
  });
  const isDocPath = !!docPathMatch;

  React.useEffect(() => {
    if (isDocPath) {
      setIsCollapsed(true);
    }
  }, [setIsCollapsed, isDocPath]);
  React.useEffect(() => {
    setIsCollapsed(true);
  }, [document.URL]);

  useEffect(() => {
    getUser();
  }, []);
  const getUser = async () => {
    const response = await fetch(
      `${devConfig.getServerBaseUrlForEnv()}/api/users/${
        userInfoByEmail?.id
      }?orgId=${getLinkedFirm()?.orgId}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        method: "GET",
      },
    );
    if (response.ok) {
      const data = await response.json();
      setUser(data);
    }
  };
  const getNavItemClasses = () => {
    return classNames(
      "w-full rounded-md block flex items-center bg-transparent text-[#9FA1AD] my-2 py-2 px-4 overflow-hidden text-base cursor-pointer",
      "focus:bg-gray-100 focus:font-semibold focus:text-primary focus:text-gray-900 focus:outline-none focus:shadow-outline",
      "hover:bg-gray-100 hover:text-[#7C66F0]",
    );
  };

  const userRoleNavIcons = (name: string) => {
    if (name === "support" && !supportAccess?.canUse) {
      return false;
    }
    if (name === "activity-log" && !canViewActivityLog) {
      return false;
    }
    if (name === "reports" && !canOpenReports) {
      return false;
    }
    if (name === "billing" && !canViewBilling) {
      return false;
    }
    if (user?.userRoleEnum?.userRole?.includes("Billing")) {
      if (
        location.pathname === "/billing/invoices" ||
        location.pathname === "/billing/payments"
      ) {
        navigate("/");
      }
      if (location.pathname.includes("settings")) {
        navigate("/");
      }
      return name !== "settings" && name !== "billing";
    }
    return true;
  };
  const getLocation = () => {
    if (location) {
      return location.pathname ? location.pathname.split("/")[1] : "";
    } else return "";
  };
  const sideNavRouteLinks = sideNavRouteMap.map((routeElement, index) => {
    const { name, label, icon: Icon, path } = routeElement;
    const navText = "navText" in routeElement ? routeElement.navText : name;

    return (
      <React.Fragment key={index}>
        {userRoleNavIcons(name) && (
          <Link
            to={`/${path}`}
            className={"w-full"}
            onClick={() => onMobileClose?.()}>
            <div
              className={`${getNavItemClasses()} ${
                getLocation().includes(name) && "bg-gray-200 !text-[#7C66F0]"
              }`}
              data-tooltip-id={path}
              key={name}>
              <div className={"relative flex items-center gap-4"}>
                <Icon className={"h-6 w-6"} />
                {name === "documents" && !!filesSummary?.newFromClients && (
                  <span
                    aria-label={`${filesSummary.newFromClients} new from clients`}
                    className={
                      "absolute -top-2 left-3.5 min-w-[18px] rounded-full bg-[#6B4EFF] px-1 text-center text-[10px] font-bold leading-[18px] text-white"
                    }>
                    {filesSummary.newFromClients > 99
                      ? "99+"
                      : filesSummary.newFromClients}
                  </span>
                )}
                {name === "support" && !!supportCount?.needsYou && (
                  <span
                    aria-label={`${supportCount.needsYou} need you`}
                    className={
                      "absolute -top-2 left-3.5 min-w-[18px] rounded-full bg-[#6B4EFF] px-1 text-center text-[10px] font-bold leading-[18px] text-white"
                    }>
                    {supportCount.needsYou > 99 ? "99+" : supportCount.needsYou}
                  </span>
                )}
                <div className={"whitespace-nowrap capitalize"}>{navText}</div>
              </div>
            </div>
          </Link>
        )}
        {!isOpenMenu && !isMobileOpen && (
          <ReactTooltip
            id={path}
            place={"right"}
            content={label}
            className={"z-10 !rounded-[8px] !p-2 !text-sm capitalize"}
          />
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      {isMobileOpen && (
        <div
          aria-hidden={true}
          onClick={() => onMobileClose?.()}
          className={"fixed inset-0 top-16 z-[44] bg-[#1F2433]/30 md:hidden"}
        />
      )}
      <div
        className={classNames(
          isOpenMenu ? "w-fit" : "w-20",
          "flex max-h-full flex-col justify-between border-r border-gray-300 bg-white transition-all duration-300",
          // Phones: an overlay with labels, opened from the top bar.
          "max-md:fixed max-md:bottom-0 max-md:left-0 max-md:top-16 max-md:z-[45] max-md:max-h-none max-md:w-64 max-md:overflow-y-auto max-md:shadow-xl",
          !isMobileOpen && "max-md:!hidden",
        )}
        // style={{ transition: "width 300ms cubic-bezier(0.2, 0, 0, 1) 0s" }}
        /*onMouseEnter={onMouseOver}
                    onMouseLeave={onMouseOver}*/
      >
        <div>
          <div className={"absolute flex items-center justify-between"}>
            <div className={"flex items-center pl-1 opacity-0"}>
              <span>
                <LocationSelector isHidden={isCollapsed} />
              </span>
            </div>
          </div>
          <div className={"flex flex-col justify-between"}>
            <div className={"flex flex-col items-center"}>
              <div
                className={
                  "relative mt-2 flex w-full border-b border-gray-300 px-2  "
                }
                ref={openButtonRef}>
                <div
                  className={
                    "mb-2 flex w-full cursor-pointer overflow-hidden py-2 hover:rounded-md hover:bg-gray-100"
                  }
                  onClick={() => setIsOpenAdd(!isOpenAdd)}>
                  <div className={"px-4"}>
                    <button
                      className={`${
                        isOpenAdd ? "bg-[#1F2937]" : "bg-purple"
                      } border-purple flex h-[30px] !w-[30px] items-center justify-center rounded-lg border-solid text-[20px] text-white duration-300`}>
                      <div
                        className={`${
                          isOpenAdd && "rotate-[-135deg]"
                        } fill-white duration-300`}>
                        <PlusSvg />
                      </div>
                    </button>
                  </div>
                  <div
                    className={
                      "flex items-center pl-1 text-base text-[#7C66F0]"
                    }>
                    Create
                  </div>
                </div>

                {isOpenAdd && (
                  <CreateMenu
                    canViewBilling={canViewBilling}
                    onPick={pickCreate}
                    onClose={() => setIsOpenAdd(false)}
                  />
                )}
              </div>

              <div className={"w-full px-3"}>
                {sideNavRouteLinks}
                {/* Support link removed (Roadmap 1855): the round Help chat
                    button (bottom right) replaces it. */}
              </div>
            </div>
          </div>
        </div>
        <div
          className={
            "flex flex-col gap-4 border-t border-gray-300 px-3 py-4 max-md:!hidden"
          }>
          <div
            onClick={() => setIsOpenMenu(!isOpenMenu)}
            className={
              "flex h-[40px] cursor-pointer items-center overflow-hidden px-4 hover:rounded-md hover:bg-gray-200"
            }
            data-tooltip-id={"menu"}>
            <div className={"flex items-center gap-4"}>
              {isOpenMenu ? (
                <LuArrowLeftToLine
                  className={"h-[24px] w-[24px] text-[#9FA1AD]"}
                />
              ) : (
                <>
                  <LuArrowRightToLine
                    className={"h-[24px] w-[24px] text-[#9FA1AD]"}
                  />
                  <ReactTooltip
                    id={"menu"}
                    place={"right"}
                    content={"Maximize menu"}
                    className={"z-10 !rounded-[8px] !p-2 !text-sm capitalize"}
                  />
                </>
              )}
              <div className={"w-[110px] text-base text-[#9FA1AD]"}>
                Minimize menu
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAddWork && (
        <AddWorkModal onClose={() => setIsAddWork(false)} isOpen={isAddWork} />
      )}
      {isAddContact && (
        <AddContactModal
          key={contactType}
          isOpen={isAddContact}
          initialType={contactType}
          closeModal={() => setIsAddContact(false)}
          isSearch={false}
        />
      )}
      {/* QA batch 15 (TIME-015): New Time = time already worked; a timer is one click away. */}
      {isAddTime && (
        <AddTimeEntry
          closeModal={() => setIsAddTime(false)}
          onStartTimer={() => {
            setIsAddTime(false);
            setIsStartTimer(true);
          }}
        />
      )}
      {isStartTimer && (
        <StartTimer
          isOpen={isStartTimer}
          closeModal={() => setIsStartTimer(false)}
        />
      )}
    </>
  );
};
